import React, { useEffect, useState } from 'react';
import { useParams, useOutletContext } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTrip } from '../contexts/TripContext';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Checkbox } from '../components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Plus, Vote as VoteIcon, Loader2, CheckCircle2, Users } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { ptBR, enUS } from 'date-fns/locale';
import { toast } from 'sonner';
import { PageHeader } from '../components/common/PageHeader';

export default function TripVotes() {
  const { tripId } = useParams();
  const context = useOutletContext();
  const { isOrganizer } = context || {};
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const dateLocale = language === 'en-US' ? enUS : ptBR;
  const { votes, fetchVotes, createVote, castVote, loading } = useTrip();
  
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    options: ['', ''],
    allow_multiple: false,
  });

  useEffect(() => {
    if (tripId) {
      fetchVotes(tripId);
    }
  }, [tripId, fetchVotes]);

  const handleAddOption = () => {
    setFormData({ ...formData, options: [...formData.options, ''] });
  };

  const handleOptionChange = (index, value) => {
    const newOptions = [...formData.options];
    newOptions[index] = value;
    setFormData({ ...formData, options: newOptions });
  };

  const handleRemoveOption = (index) => {
    if (formData.options.length > 2) {
      const newOptions = formData.options.filter((_, i) => i !== index);
      setFormData({ ...formData, options: newOptions });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validOptions = formData.options.filter(opt => opt.trim());
    if (!formData.title || validOptions.length < 2) {
      toast.error('Preencha o título e pelo menos 2 opções');
      return;
    }

    try {
      await createVote(tripId, {
        title: formData.title,
        description: formData.description || null,
        options: validOptions,
        allow_multiple: formData.allow_multiple,
      });
      toast.success('Votação criada!');
      setDialogOpen(false);
      setFormData({
        title: '',
        description: '',
        options: ['', ''],
        allow_multiple: false,
      });
    } catch (error) {
      toast.error('Erro ao criar votação');
    }
  };

  const handleVote = async (voteId, selectedOptions) => {
    try {
      await castVote(tripId, voteId, selectedOptions);
      toast.success('Voto registrado!');
    } catch (error) {
      toast.error('Erro ao votar');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        title="Votações"
        subtitle="Tome decisões em grupo de forma democrática"
        helpScreen="votes"
        actions={
          isOrganizer && (
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button 
                  className="rounded-full bg-orange-500 hover:bg-orange-600"
                  data-testid="create-vote-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Nova votação
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>Criar votação</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                  <div>
                    <Label htmlFor="title">Pergunta *</Label>
                    <Input
                      id="title"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      placeholder="Ex: O que vamos fazer no primeiro dia?"
                      className="mt-1"
                      data-testid="vote-title"
                    />
                  </div>

                  <div>
                    <Label htmlFor="description">Descrição</Label>
                    <Textarea
                      id="description"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Mais detalhes sobre a votação..."
                      className="mt-1"
                      rows={2}
                    />
                  </div>

                  <div>
                    <Label>Opções *</Label>
                    <div className="space-y-2 mt-2">
                      {formData.options.map((option, index) => (
                        <div key={index} className="flex gap-2">
                          <Input
                            value={option}
                            onChange={(e) => handleOptionChange(index, e.target.value)}
                            placeholder={`Opção ${index + 1}`}
                            data-testid={`vote-option-${index}`}
                          />
                          {formData.options.length > 2 && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              onClick={() => handleRemoveOption(index)}
                            >
                              ×
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="mt-2"
                      onClick={handleAddOption}
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Adicionar opção
                    </Button>
                  </div>

                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="allow_multiple"
                      checked={formData.allow_multiple}
                      onCheckedChange={(checked) => setFormData({ ...formData, allow_multiple: checked })}
                    />
                    <Label htmlFor="allow_multiple" className="font-normal">
                      Permitir múltiplas escolhas
                    </Label>
                  </div>

                  <div className="flex gap-3 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      className="flex-1 rounded-full"
                      onClick={() => setDialogOpen(false)}
                    >
                      Cancelar
                    </Button>
                    <Button
                      type="submit"
                      className="flex-1 rounded-full bg-orange-500 hover:bg-orange-600"
                      data-testid="save-vote-btn"
                    >
                      Criar
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          )
        }
      />

      {/* Votes List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
        </div>
      ) : votes.length === 0 ? (
        <Card className="border-slate-100 dark:border-slate-700 border-dashed">
          <CardContent className="py-12 text-center">
            <VoteIcon className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="font-heading font-bold text-lg text-slate-900 dark:text-white mb-2">
              Nenhuma votação
            </h3>
            <p className="text-slate-500 dark:text-slate-400">
              Crie votações para decidir detalhes da viagem em grupo
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {votes.map((vote, index) => (
            <VoteCard
              key={vote.id}
              vote={vote}
              index={index}
              onVote={handleVote}
              userId={user?.id}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function VoteCard({ vote, index, onVote, userId }) {
  const [selectedOptions, setSelectedOptions] = useState(vote.my_vote || []);
  const hasVoted = vote.my_vote !== null && vote.my_vote !== undefined;
  const isActive = vote.status === 'active';

  const handleSelect = (optionIndex) => {
    if (!isActive) return;

    if (vote.allow_multiple) {
      setSelectedOptions(prev => 
        prev.includes(optionIndex)
          ? prev.filter(i => i !== optionIndex)
          : [...prev, optionIndex]
      );
    } else {
      setSelectedOptions([optionIndex]);
    }
  };

  const handleSubmitVote = () => {
    if (selectedOptions.length > 0) {
      onVote(vote.id, selectedOptions);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
    >
      <Card className="border-slate-100 dark:border-slate-700">
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between">
            <div>
              <CardTitle className="text-lg">{vote.title}</CardTitle>
              {vote.description && (
                <p className="text-sm text-slate-500 mt-1">{vote.description}</p>
              )}
            </div>
            <Badge className={vote.status === 'active' ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' : 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300'}>
              {vote.status === 'active' ? 'Ativa' : 'Encerrada'}
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {vote.results?.map((result, idx) => {
              const isSelected = selectedOptions.includes(result.index);
              const isMyVote = vote.my_vote?.includes(result.index);
              
              return (
                <div
                  key={idx}
                  className={`p-3 rounded-xl border transition-all cursor-pointer ${
                    isSelected
                      ? 'border-orange-500 bg-orange-50'
                      : 'border-slate-100 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
                  }`}
                  onClick={() => handleSelect(result.index)}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        isSelected ? 'border-orange-500 bg-orange-500' : 'border-slate-300'
                      }`}>
                        {isSelected && <CheckCircle2 className="w-3 h-3 text-white" />}
                      </div>
                      <span className="font-medium text-slate-900 dark:text-white">{result.option}</span>
                      {isMyVote && hasVoted && (
                        <Badge variant="outline" className="text-xs">Seu voto</Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4 text-slate-400" />
                      <span className="text-sm text-slate-600 dark:text-slate-400">{result.count}</span>
                      <span className="text-sm text-slate-400">({result.percentage}%)</span>
                    </div>
                  </div>
                  <Progress value={result.percentage} className="h-2" />
                </div>
              );
            })}
          </div>

          {isActive && !hasVoted && selectedOptions.length > 0 && (
            <Button
              className="w-full mt-4 rounded-full bg-orange-500 hover:bg-orange-600"
              onClick={handleSubmitVote}
              data-testid={`submit-vote-${vote.id}`}
            >
              Votar
            </Button>
          )}

          <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between text-sm text-slate-500 dark:text-slate-400">
            <span>{vote.total_votes} votos</span>
            {vote.allow_multiple && (
              <span>Múltipla escolha</span>
            )}
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}
